/**
 * Version and signature chrome for the ICS-207, mounted on the roster.
 *
 * The 207 lives here rather than in the IAP planning view because its subject
 * is the organization chart, and the chart is authored on this page. Box 4 of
 * the Coast Guard sheet is a picture of it rather than a grid of filled seats,
 * so signing captures that picture and stores it: without a stored image the
 * IAP could only ever bundle the seat-by-seat fill, which is not what the
 * signer approved.
 */

import React, { useCallback } from 'react';
import { toast } from 'react-toastify';
import * as Sentry from '@sentry/react';

import FormVersioningPanel from 'components/IAP/femaCards/FormVersioningPanel';
import { IcsPreparedByBox } from 'components/IAP/planningView/widgets/IcsPreparedByBox';
import { useActualIncidentId } from 'components/IAP/hooks/commonHooks';
import { useWorkingPeriodNumber } from 'components/IAP/hooks/incidentHooks';
import {
  useIncidentInfo,
  useUpdateIcsInfo,
} from 'components/IAP/hooks/incidentInfoHooks';
import { uploadFilesToBackend } from 'components/FileManagement/hooks/fileMutations';
import { isVersionedForm } from 'components/IAP/femaCards/formSectionSchema';

export const ICS_207_BASE_ID = 'ICS_207';

/**
 * Numbered as the 207-CG numbers it, which puts the chart in block 4 and the
 * signature under it.
 */
const ICS207_PREPARED_BY_BOX_LABEL = '5. Prepared by';

type Ics207SigningPanelProps = {
  /**
   * Produces a PNG of the chart exactly as it is currently filtered and laid
   * out.
   */
  captureChart: () => Promise<File | null>;
};

export const Ics207SigningPanel: React.FC<Ics207SigningPanelProps> = ({
  captureChart,
}) => {
  const incidentId = useActualIncidentId();
  const periodNumber = useWorkingPeriodNumber();
  const { data: incidentInfo } = useIncidentInfo();
  const updateIncidentInfo = useUpdateIcsInfo();

  /**
   * Stores the chart, then lets the signature proceed.
   *
   * The order matters: the signed version snapshots `ics207_org_chart_file_id`,
   * so the upload and the incident info write both have to land first. A
   * failure anywhere here abandons the signature rather than recording one
   * against a chart that was never saved.
   */
  const captureAndStoreChart = useCallback(async () => {
    if (!incidentId) return false;
    try {
      const chart = await captureChart();
      if (!chart) {
        toast.error('There is nothing on the org chart to sign.');
        return false;
      }

      const [uploaded] = await uploadFilesToBackend({
        files: [chart],
        workspaceId: incidentId,
        // A chart screenshot has no text worth retrieving, and indexing it
        // would only put noise in front of the assistant.
        ragIndex: false,
      });
      if (!uploaded?.id) {
        toast.error('Could not save the organization chart.');
        return false;
      }

      await updateIncidentInfo.mutateAsync({
        ...incidentInfo,
        ics207OrgChartFileId: uploaded.id,
      });
      return true;
    } catch (error) {
      Sentry.captureException(error);
      toast.error('Could not capture the organization chart.');
      return false;
    }
  }, [captureChart, incidentId, incidentInfo, updateIncidentInfo]);

  if (!isVersionedForm(ICS_207_BASE_ID)) return null;

  return (
    <div
      className="tw:flex tw:flex-col tw:gap-3"
      data-testid="ics207-signing-panel"
    >
      <FormVersioningPanel
        baseId={ICS_207_BASE_ID}
        periodNumber={periodNumber}
        onBeforeSign={captureAndStoreChart}
      />
      <IcsPreparedByBox
        baseId={ICS_207_BASE_ID}
        periodNumber={periodNumber}
        boxLabel={ICS207_PREPARED_BY_BOX_LABEL}
        testId="ics207-section-prepared-by"
        unsignedMessage="Not signed. Creating a signed version stores a picture of this chart and prints it in box 4 of the ICS-207 and the IAP."
      />
    </div>
  );
};

export default Ics207SigningPanel;
